import React from 'react';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import cn from '@/utils/cn';
import type { ChatMessage, PromptDetail } from './types';

interface Props {
  onChange: (prompt: string | ChatMessage[]) => void;
  prompt: PromptDetail;
  readOnly: boolean;
}

const codeTextClass = 'tw-font-mono tw-text-[13px] tw-leading-normal';

const textareaClass = cn(
  'tw-block tw-w-full tw-box-border tw-resize-y tw-overflow-hidden tw-rounded-md tw-border tw-border-solid',
  'tw-border-spa-border-input tw-bg-spa-bg-code tw-px-4 tw-py-3 tw-text-spa-on-bg',
  'tw-outline-none focus:tw-border-spa-accent',
  codeTextClass,
);

// Grows the textarea to its content so toggling Edit keeps the read-only box's height.
const autosize = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  const { style } = el;
  style.height = 'auto';
  style.height = `${el.scrollHeight}px`;
};

// Read-only text shares the editor's padding and type, so toggling Edit doesn't shift the page.
const readOnlyClass = cn(
  'tw-m-0 tw-box-border tw-whitespace-pre-wrap tw-break-words tw-rounded-md tw-border tw-border-solid',
  'tw-border-spa-border-secondary tw-bg-spa-bg-code tw-px-4 tw-py-3 tw-text-spa-primary',
  codeTextClass,
);

interface PromptTextProps {
  className?: string;
  label: string;
  minHeightClass: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  value: string;
}

const PromptText = ({
  className, label, minHeightClass, onChange, readOnly, value,
}: PromptTextProps) => (readOnly ? (
  <pre className={cn(readOnlyClass, minHeightClass, className)}>
    {value || <span className="tw-text-spa-tertiary">(empty)</span>}
  </pre>
) : (
  <textarea
    ref={autosize}
    aria-label={label}
    className={cn(textareaClass, minHeightClass, className)}
    value={value}
    onChange={(e) => {
      autosize(e.currentTarget);
      onChange(e.target.value);
    }}
  />
));

interface ChatMessageEditorProps {
  message: ChatMessage;
  onChange: (updated: ChatMessage) => void;
  readOnly: boolean;
}

const ChatMessageEditor = ({ message, onChange, readOnly }: ChatMessageEditorProps) => (
  <div className="tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-spa-border-secondary">
    <div
      className={cn(
        'tw-border-0 tw-border-b tw-border-solid tw-border-spa-border-secondary tw-bg-spa-bg-tertiary',
        'tw-px-4 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-secondary',
      )}
    >
      {message.role}
    </div>
    <PromptText
      className="tw-rounded-none tw-border-0 focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-spa-accent"
      label={`${message.role} message`}
      minHeightClass="tw-min-h-[5rem]"
      readOnly={readOnly}
      value={message.content}
      onChange={content => onChange({ ...message, content })}
    />
  </div>
);

const PromptEditor = ({ onChange, prompt, readOnly }: Props) => {
  if (prompt.type === 'chat' && Array.isArray(prompt.prompt)) {
    const messages = prompt.prompt as ChatMessage[];
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        {messages.map((message, index) => (
          <ChatMessageEditor
            key={`${message.role}-${index}`}
            message={message}
            readOnly={readOnly}
            onChange={(updated) => {
              const next = [...messages];
              next[index] = updated;
              onChange(next);
            }}
          />
        ))}
        {!readOnly && (
          <SPAButton
            className="tw-self-start"
            size="xs"
            onClick={() => onChange([...messages, { content: '', role: 'user' }])}
          >
            Add message
          </SPAButton>
        )}
      </div>
    );
  }

  return (
    <PromptText
      label="Prompt text"
      minHeightClass="tw-min-h-[12rem]"
      readOnly={readOnly}
      value={typeof prompt.prompt === 'string' ? prompt.prompt : ''}
      onChange={onChange}
    />
  );
};

export default PromptEditor;
